import { useMyTaskCount } from '../../data/hooks';
import { todayKey } from '../record/dueDate';
import { NavCount } from './NavCount';

/**
 * The live count of OPEN TASKS ASSIGNED TO YOU (notes-tasks-comments.md 12.2).
 *
 * The same shape as `PendingBadge`, and for the same reasons: one component with its own
 * query so both navigations can render it, the TanStack cache dedupes the request, and
 * the SSE record notice invalidates the key - which is what makes the number move when a
 * colleague assigns you something, without a poll.
 *
 * Nothing is rendered at zero. A badge showing "0" is noise that trains people to ignore
 * the badge.
 *
 * D9 makes this safe to show unconditionally: your own tasks are always visible to you,
 * so a count of them leaks nothing you could not see by opening the screen.
 */
/**
 * TWO PLACES, TWO PARTS, TWO COUNTS SINCE 2026-10-04.
 *
 * In the desktop sidebar's My tasks ROW the count is what needs doing now: the person's open
 * tasks that are overdue or due today (redesign register R-S2, "My tasks = overdue and due
 * today"), a grey number at the right with no capsule (`NavCount`). `dueTo` is today, so an
 * undated task is not counted: it is neither overdue nor due today.
 *
 * On the phone TAB BAR it stays Apple's badge with every open task, until the phone round (P6):
 * the red capsule on the symbol's top trailing corner, a different part from a row's count (the
 * kit ships the badge with its own fill and its own white count, `App Icons/- Badge/Badge`).
 */
export function TaskBadge({ variant = 'row' }: { readonly variant?: 'row' | 'tab' }) {
  return variant === 'tab' ? <TabBadge /> : <RowCount />;
}

function RowCount() {
  const count = useMyTaskCount({ assignee: 'me', dueTo: todayKey() });
  return <NavCount value={count.data ?? null} />;
}

function TabBadge() {
  const count = useMyTaskCount();
  const open = count.data ?? 0;
  if (open === 0) return null;
  const label = `${open} open tasks assigned to you`;
  return (
    <>
      {/* The capsule is the picture of the count; the sentence beside it is what a
          screen reader announces, so the tab reads "Tasks, 288 open tasks assigned to
          you" instead of "Tasks 288". */}
      <span className="shell-tab-badge" aria-hidden="true">
        {open}
      </span>
      <span className="sr-only">{label}</span>
    </>
  );
}
